import React, { useRef, useState } from "react";
import api, { formatApiError, API_BASE } from "@/lib/upr-api";
import { toast } from "sonner";
import { Download, Upload, FileSpreadsheet, AlertTriangle, Check, Image as ImageIcon, Music } from "lucide-react";

/**
 * Admin → Import tab. Lets the label drag-and-drop an .xlsx catalog and
 * preview/commit the import.
 */
export default function ImportAdmin() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const onFile = (f) => {
    setFile(f || null);
    setPreview(null);
    setResult(null);
  };

  const downloadTemplate = async () => {
    try {
      const r = await fetch(`${API_BASE}/import/template`, {
        credentials: "include",
      });
      if (!r.ok) throw new Error("Could not fetch template");
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "universo_positivo_catalog_template.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch (e) {
      toast.error(e.message || "Download failed");
    }
  };

  const doPreview = async () => {
    if (!file) { toast.error("Pick a file first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/import/preview", fd);
      setPreview(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not parse the file");
    } finally {
      setBusy(false);
    }
  };

  const doCommit = async () => {
    if (!file) return;
    const summary = preview?.summary;
    if (summary && !window.confirm(
      `This will create ${summary.releases_to_create} releases and ${summary.artists_to_create} artists. Continue?`
    )) return;
    setCommitting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/import/commit", fd);
      setResult(data);
      toast.success(`Imported ${data.created_releases} releases and ${data.created_artists} artists.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Import failed");
    } finally {
      setCommitting(false);
    }
  };

  return (
    <section data-testid="import-page">
      <div className="flex items-baseline justify-between mb-6">
        <h2 className="font-heading font-extrabold text-2xl tracking-tight">Import catalog</h2>
        <button onClick={downloadTemplate} className="upr-btn upr-btn-ghost" data-testid="download-template">
          <Download size={14} /> Download template.xlsx
        </button>
      </div>

      <p className="text-sm mb-6" style={{ color: "var(--upr-muted)" }}>
        Upload an Excel workbook to bulk-create artists and releases. Columns are matched case-insensitively;
        sensible aliases work (e.g. <code>Cat. #</code>, <code>Release Title</code>, <code>Version</code>, <code># of track</code>, <code>Release date / Hour / Time zone</code>, <code>UPC</code>).
        Rows with an already-existing <code>catalog_number</code> are skipped. Covers + photos + audio are placeholders you upload later from the Releases/Artists tabs.
      </p>

      {/* File picker */}
      <div className="border-2 border-dashed p-8 text-center" style={{ borderColor: "var(--upr-border)", background: "var(--upr-surface)" }}
        onDragOver={(e) => { e.preventDefault(); }}
        onDrop={(e) => {
          e.preventDefault();
          const f = e.dataTransfer.files?.[0];
          if (f) onFile(f);
        }}
        data-testid="import-dropzone"
      >
        <FileSpreadsheet size={32} style={{ color: "var(--upr-muted)" }} className="mx-auto mb-3" />
        {file ? (
          <div>
            <div className="font-heading font-extrabold">{file.name}</div>
            <div className="upr-eyebrow mt-1">{Math.round(file.size / 1024)} KB</div>
            <button onClick={() => onFile(null)} className="upr-link-underline text-xs mt-3" data-testid="import-clear">Choose another</button>
          </div>
        ) : (
          <>
            <div className="font-heading font-extrabold">Drop your .xlsx here</div>
            <div className="text-sm" style={{ color: "var(--upr-muted)" }}>or</div>
            <button className="upr-btn mt-3" onClick={() => fileRef.current?.click()} data-testid="import-browse">
              <Upload size={14} /> Choose file
            </button>
          </>
        )}
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="hidden"
          onChange={(e) => onFile(e.target.files?.[0])}
          data-testid="import-file"
        />
      </div>

      {file && (
        <div className="mt-6 flex gap-3">
          <button onClick={doPreview} disabled={busy} className="upr-btn" data-testid="import-preview">
            {busy ? "Parsing…" : "1. Preview"}
          </button>
          <button onClick={doCommit} disabled={!preview || committing} className="upr-btn" data-testid="import-commit">
            {committing ? "Importing…" : "2. Import to site"}
          </button>
        </div>
      )}

      {preview && (
        <div className="mt-10" data-testid="import-preview-result">
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <Stat label="Releases — to create" value={preview.summary.releases_to_create} accent />
            <Stat label="Releases — skipped (already exist)" value={preview.summary.releases_skipped} />
            <Stat label="Artists — to create" value={preview.summary.artists_to_create} accent />
            <Stat label="Artists — skipped (already exist)" value={preview.summary.artists_skipped} />
          </div>

          {preview.warnings && preview.warnings.length > 0 && (
            <div className="p-4 mb-6" style={{ background: "var(--upr-surface)", border: "1px solid var(--upr-border)" }}>
              <div className="upr-eyebrow mb-2 inline-flex items-center gap-1"><AlertTriangle size={12} /> Warnings</div>
              <ul className="text-sm space-y-1" style={{ color: "var(--upr-muted)" }}>
                {preview.warnings.map((w, i) => <li key={i}>• {w}</li>)}
              </ul>
            </div>
          )}

          <h3 className="font-heading font-extrabold text-lg tracking-tight mb-3">Releases preview</h3>
          <div className="overflow-x-auto" style={{ border: "1px solid var(--upr-border)" }}>
            <table className="w-full text-sm" data-testid="import-releases-table">
              <thead>
                <tr style={{ background: "var(--upr-surface)" }}>
                  <Th>Status</Th><Th>Cat. #</Th><Th>Title</Th><Th>Version</Th><Th>Artist</Th><Th>Date</Th><Th>Tracks</Th><Th>UPC</Th>
                </tr>
              </thead>
              <tbody>
                {preview.releases.map((r, i) => (
                  <tr key={i} style={{ borderTop: "1px solid var(--upr-border)", background: r.action === "skip" ? "var(--upr-surface)" : "transparent" }}>
                    <Td>{r.action === "create" ? <span style={{ color: "var(--upr-secondary)" }} className="upr-eyebrow inline-flex items-center gap-1"><Check size={10} /> NEW</span> : <span className="upr-eyebrow" style={{ color: "var(--upr-muted)" }}>SKIP</span>}</Td>
                    <Td><code style={{ fontFamily: "monospace" }}>{r.catalog_number || "—"}</code></Td>
                    <Td className="font-heading font-extrabold">{r.title}</Td>
                    <Td>{r.version || "—"}</Td>
                    <Td>{r.artist_name}</Td>
                    <Td>{r.release_date || "—"}</Td>
                    <Td>{(r.tracklist || []).length}</Td>
                    <Td><code style={{ fontFamily: "monospace", fontSize: "0.7rem" }}>{r.upc || "—"}</code></Td>
                  </tr>
                ))}
                {preview.releases.length === 0 && (
                  <tr><Td colSpan={8}><div className="p-4 text-center" style={{ color: "var(--upr-muted)" }}>No releases parsed.</div></Td></tr>
                )}
              </tbody>
            </table>
          </div>

          <h3 className="font-heading font-extrabold text-lg tracking-tight mt-8 mb-3">Artists preview</h3>
          <div className="overflow-x-auto" style={{ border: "1px solid var(--upr-border)" }}>
            <table className="w-full text-sm" data-testid="import-artists-table">
              <thead>
                <tr style={{ background: "var(--upr-surface)" }}>
                  <Th>Status</Th><Th>Name</Th><Th>Bio</Th>
                </tr>
              </thead>
              <tbody>
                {preview.artists.map((a, i) => (
                  <tr key={i} style={{ borderTop: "1px solid var(--upr-border)", background: a.action === "skip" ? "var(--upr-surface)" : "transparent" }}>
                    <Td>{a.action === "create" ? <span style={{ color: "var(--upr-secondary)" }} className="upr-eyebrow">NEW</span> : <span className="upr-eyebrow" style={{ color: "var(--upr-muted)" }}>SKIP</span>}</Td>
                    <Td className="font-heading font-extrabold">{a.name}</Td>
                    <Td>{(a.bio || "—").slice(0, 80)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {result && (
        <div className="mt-10 p-5" style={{ background: "var(--upr-surface)", border: "1px solid var(--upr-border)" }} data-testid="import-result">
          <div className="upr-eyebrow mb-2" style={{ color: "var(--upr-secondary)" }}>Import complete</div>
          <div className="font-heading font-extrabold text-xl">
            Created {result.created_releases} releases · {result.created_artists} artists
          </div>
          <div className="text-sm mt-1" style={{ color: "var(--upr-muted)" }}>
            Skipped {result.skipped_releases} duplicate release(s), {result.skipped_artists} existing artist(s).
            Covers and photos are placeholders — open each release in the Releases tab to upload its art and audio.
          </div>
        </div>
      )}

      <BulkAssetUpload />
    </section>
  );
}

/* ---------- Bulk asset upload (covers + MP3s, matched by catalog #) ---------- */
function BulkAssetUpload() {
  const fileRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const addFiles = (newFiles) => {
    const arr = Array.from(newFiles || []);
    setFiles((prev) => [...prev, ...arr]);
    setResult(null);
  };

  const removeAt = (i) => setFiles((prev) => prev.filter((_, k) => k !== i));

  const upload = async () => {
    if (files.length === 0) return;
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/import/assets", fd, { timeout: 600000 });
      setResult(data);
      toast.success(`Updated ${data.updated_releases} release(s). ${data.unmatched_count} file(s) unmatched.`);
      setFiles([]);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mt-20" data-testid="bulk-assets-section">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="font-heading font-extrabold text-2xl tracking-tight">Bulk asset upload</h2>
        <div className="upr-eyebrow">{files.length} file{files.length === 1 ? "" : "s"} selected</div>
      </div>
      <p className="text-sm mb-5" style={{ color: "var(--upr-muted)" }}>
        Drop any number of files at once — each file is matched to a release by the catalog-number prefix in its filename:
        <br/><code className="text-xs">UP001.jpg</code> → cover for UP001 · <code className="text-xs">UP001-01_Title.mp3</code> → track 1 of UP001 · <code className="text-xs">UP002-02.mp3</code> → track 2 of UP002. Extensions: jpg/png/webp/gif for covers; mp3/wav/ogg/flac for audio.
      </p>

      <div
        className="border-2 border-dashed p-8 text-center"
        style={{ borderColor: "var(--upr-border)", background: "var(--upr-surface)" }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
        data-testid="bulk-dropzone"
      >
        <Upload size={28} style={{ color: "var(--upr-muted)" }} className="mx-auto mb-3" />
        <div className="font-heading font-extrabold">Drop covers & MP3s here</div>
        <div className="text-sm" style={{ color: "var(--upr-muted)" }}>or</div>
        <button className="upr-btn mt-3" onClick={() => fileRef.current?.click()} data-testid="bulk-browse">
          Choose files
        </button>
        <input
          ref={fileRef}
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp,image/gif,audio/mpeg,audio/mp3,audio/wav,audio/ogg,audio/flac"
          className="hidden"
          onChange={(e) => addFiles(e.target.files)}
          data-testid="bulk-file"
        />
      </div>

      {files.length > 0 && (
        <div className="mt-4 max-h-80 overflow-y-auto" style={{ border: "1px solid var(--upr-border)" }} data-testid="bulk-queue">
          {files.map((f, i) => {
            const isAudio = (f.type || "").startsWith("audio") || /\.(mp3|wav|ogg|flac|m4a)$/i.test(f.name);
            const Icon = isAudio ? Music : ImageIcon;
            return (
              <div key={i} className="flex items-center gap-3 px-3 py-2 border-b text-sm" style={{ borderColor: "var(--upr-border)" }}>
                <Icon size={14} style={{ color: "var(--upr-muted)" }} />
                <span className="flex-1 truncate font-mono" style={{ fontSize: "0.78rem" }}>{f.name}</span>
                <span className="upr-eyebrow shrink-0" style={{ fontSize: "0.6rem" }}>{Math.round(f.size / 1024)} KB</span>
                <button type="button" onClick={() => removeAt(i)} className="upr-eyebrow hover:text-[color:var(--upr-primary)]" data-testid={`bulk-remove-${i}`}>×</button>
              </div>
            );
          })}
        </div>
      )}

      {files.length > 0 && (
        <div className="mt-4">
          <button onClick={upload} disabled={busy} className="upr-btn" data-testid="bulk-upload">
            {busy ? "Uploading…" : `Upload ${files.length} file${files.length === 1 ? "" : "s"}`}
          </button>
        </div>
      )}

      {result && (
        <div className="mt-6 p-5" style={{ background: "var(--upr-surface)", border: "1px solid var(--upr-border)" }} data-testid="bulk-result">
          <div className="upr-eyebrow mb-2" style={{ color: "var(--upr-secondary)" }}>Bulk upload complete</div>
          <div className="font-heading font-extrabold text-xl">
            {result.matched_count} / {result.total_received} files matched · {result.updated_releases} release(s) updated
          </div>
          {result.unmatched && result.unmatched.length > 0 && (
            <div className="mt-3">
              <div className="upr-eyebrow mb-1 inline-flex items-center gap-1" style={{ color: "var(--upr-primary)" }}><AlertTriangle size={12} /> Unmatched files</div>
              <ul className="text-xs mt-1 space-y-0.5" style={{ color: "var(--upr-muted)" }}>
                {result.unmatched.map((u, i) => (
                  <li key={i}><code style={{ fontFamily: "monospace" }}>{u.filename}</code> — {u.reason}</li>
                ))}
              </ul>
            </div>
          )}
          {result.updated && result.updated.length > 0 && (
            <div className="mt-3">
              <div className="upr-eyebrow mb-1">Updated releases</div>
              <ul className="text-xs mt-1 space-y-0.5" style={{ color: "var(--upr-muted)" }}>
                {result.updated.map((u, i) => (
                  <li key={i}>
                    <code style={{ fontFamily: "monospace" }}>{u.catalog}</code>
                    {u.cover_set ? " — cover updated" : ""}{u.cover_set && u.tracks_added ? " · " : ""}{u.tracks_added ? `${u.tracks_added} track(s) added` : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

const Stat = ({ label, value, accent }) => (
  <div className="p-4 border" style={{ borderColor: "var(--upr-border)" }}>
    <div className="upr-eyebrow">{label}</div>
    <div className="font-heading font-black text-3xl mt-1" style={{ color: accent ? "var(--upr-primary)" : "var(--upr-fg)", letterSpacing: "-0.02em" }}>{value}</div>
  </div>
);

const Th = ({ children }) => <th className="text-left px-3 py-2 upr-eyebrow" style={{ fontSize: "0.6rem" }}>{children}</th>;
const Td = ({ children, colSpan, className }) => <td colSpan={colSpan} className={`px-3 py-2 align-top ${className || ""}`}>{children}</td>;
